import { maxPowerWaterAuthorityReason } from './maxPowerWaterAuthority';
import type { ReportTypeId } from '../../config/reportTypes';
import type {
  EngineeringVerdict,
  GenerationImpactComparisonProjection,
  GenerationImpactComparisonRow,
  GenerationImpactFuelSupplyProjection,
  GenerationImpactMaxPowerProjection,
  GenerationImpactReportProjection,
  GenerationMaxPowerStudySnapshot,
  GenerationImpactScenarioSnapshot,
  GenerationImpactSedAuthoritySnapshot,
  GenerationImpactWaterBasis,
  GenerationImpactScenarioInput,
} from '../../types/generationImpact';
import { deriveGasInterconnectDerate } from './gasInterconnectDerate';
import type { SiteEntity } from '../../types/site';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ProjectIntent } from '../../types/siteRag';
import type { SiteRagReport } from '../../types/siteRag';
import {
  deriveAnnualizationBasis,
  deriveGasDemandPerspective,
  deriveReliabilityPerspective,
  deriveScreeningBasis,
  footprintConventionNote,
  resolveCapacityScreenVerdict,
} from './screeningIntegrity';
import { deriveScreeningEconomics } from './screeningEconomics';
import { activeGenerationEquipmentSelection } from './equipmentSelectionState';
import {
  buildGenerationImpactTestFitProjection,
  reconcileTestFitReportWithGenerationImpact,
  reconcileTestFitReportWithoutGenerationImpact,
} from './testFitProjection';
import {
  matchWholeSiteWaterRoles,
  wholeSiteWaterLedger,
  type WholeSiteWaterLedgerEntry,
  type WholeSiteWaterLedgerKey,
} from '../water/waterQuantityRoles';
import {
  declaredWaterConsumptionProvenance,
  type DeclaredWaterConsumptionProvenance,
} from './declaredWaterConsumptionProvenance';

export interface GenerationImpactReportProjectionContext {
  site?: Pick<SiteEntity, 'siteBoundaryAggregate' | 'siteBoundaries' | 'siteBoundary' | 'siteArea'> | null;
  sed?: SiteEvidenceDocument | null;
  authority?: GenerationImpactSedAuthoritySnapshot | null;
  /**
   * Explicit non-on-site mode prevents a stale promoted snapshot on the
   * persistence carrier from re-stamping Generation & Impact onto a grid-only
   * or still-undecided Test Fit.
   */
  testFitWithoutGenerationImpact?: 'grid-only' | 'undecided';
  /**
   * The site's stored max-power study. Reports NEVER re-run it — a report is a
   * projection, not a compute step.
   */
  maxPowerStudy?: GenerationMaxPowerStudySnapshot | null;
  /** The intent stamp, used to confirm the stored study is the stamped one. */
  maxPowerStamp?: ProjectIntent['maxPowerStudy'] | null;
  /**
   * The other candidates this promotion was chosen from (promoted excluded).
   * Evidence of METHOD only — never read as a duty basis.
   */
  comparedScenarios?: readonly GenerationImpactScenarioSnapshot[] | null;
  /** Compared ids recorded on the intent that could not be loaded. */
  unresolvedComparedScenarioIds?: readonly string[] | null;
}

/** Closed role distinction derived only from the selected frozen equipment. */
export function isGridImportOnlyScenario(input: GenerationImpactScenarioInput): boolean {
  return input.units.length > 0 && input.units.every(unit => unit.technology === 'grid-import'
    && Number.isFinite(unit.count) && unit.count > 0);
}

/** True when any active unit in the scenario models a grid-import feed. */
export function hasGridImportUnits(input: GenerationImpactScenarioInput): boolean {
  return input.units.some(unit => unit.technology === 'grid-import'
    && Number.isFinite(unit.count) && unit.count > 0);
}

const GRID_SUPPLY_QUALIFICATION = 'Aggregate feed capacity is a supply envelope, not on-site generation or firm utility service. '
  + 'The modeled contingency result assumes independent feed losses; shared utility, substation, protection, '
  + 'route and other common-cause failures remain unqualified. Confirm service and independence with the utility.';

/** Add role instructions to the normal prompt without altering the frozen solve. */
export function buildGridImportRolePrompt(snapshot: GenerationImpactScenarioSnapshot): string {
  if (!isGridImportOnlyScenario(snapshot.input)) return '';
  const { input, result } = snapshot;
  const annualization = deriveAnnualizationBasis({
    facilityLoadMW: input.facilityLoadMW,
    annualOperatingHours: input.annualOperatingHours,
    normalNetMW: result.capacity.normalNetMW,
    gridImportOnly: true,
  });
  const contingency = selectedContingency(snapshot);
  return '\n[SELECTED GRID-SUPPLY ROLE — FROZEN SCENARIO]\n'
    + `Scenario ${snapshot.id}; input ${snapshot.inputHash}; result ${snapshot.resultHash}.\n`
    + `Aggregate grid-feed capacity: ${result.capacity.normalNetMW} MW; modeled ${contingency.criterion} surviving supply: `
    + `${contingency.netMW} MW; facility peak: ${input.facilityLoadMW} MW; reserve-padded requirement: ${result.capacity.reserveRequiredMW} MW.\n`
    + `${annualization.note}\n${GRID_SUPPLY_QUALIFICATION}\n`
    + 'Any separate ELCC/default capacity-credit figure is aggregate resource-adequacy planning credit. '
    + 'Describe it as modeled planning-default ELCC credit from the declared phase mix; '
    + 'do not call it the firm site target or substitute it for the selected contingency supply.\n'
    + 'Keep three roles separate: aggregate installed feed envelope, modeled surviving supply after the selected feed loss, '
    + 'and a document-stated project allocation. The first two come from this frozen scenario, not a utility allocation. '
    + 'Never replace a source-stated phase allocation with aggregate feed capacity or surviving supply, '
    + 'or manufacture a normal-case allocated-import/load/margin tuple from separate model numbers. '
    + 'Only reproduce a complete allocated-normal tuple if the current frozen originating document actually states that exact tuple; '
    + 'otherwise retain its actual operating-case role and exact citation.\n'
    + 'Every numeric per-feed service statement must call the capacity assumed, modeled or unverified in the same clause. '
    + 'A proposed feed alone is not evidence of firm service, and a document citation must not be upgraded to a verified utility commitment.\n'
    + 'Individual transformer MVA ratings are equipment nameplates, not facility apparent-power demand. '
    + 'When naming a rating in logistics, risk or other prose, keep the complete exact frozen BOM equipment label '
    + 'or attach the exact numbered document reference in that same clause. '
    + 'Do not omit the equipment role or borrow a citation from another sentence.\n'
    + '[/SELECTED GRID-SUPPLY ROLE]\n';
}

export const GENERATION_IMPACT_REPORT_TYPES = [
  'feasibility',
  'power-availability',
  'water-resource',
  'test-fit',
  'environmental',
  'public-sentiment',
  'executive-summary',
] as const satisfies readonly ReportTypeId[];

export function isGenerationImpactReportType(reportType: ReportTypeId): boolean {
  return (GENERATION_IMPACT_REPORT_TYPES as readonly ReportTypeId[]).includes(reportType);
}

function cloneSnapshot(snapshot: GenerationImpactScenarioSnapshot): GenerationImpactScenarioSnapshot {
  // Reports are JSON artifacts. Clone through JSON so optional `undefined`
  // properties are omitted now instead of being rewritten to `null` later by
  // a report persistence sanitizer, which would change the hashed input shape.
  return JSON.parse(JSON.stringify(snapshot)) as GenerationImpactScenarioSnapshot;
}

function waterAdequacy(total: number | null, available: number | undefined): EngineeringVerdict {
  if (total == null || available == null) return 'indeterminate';
  return total <= available ? 'pass' : 'fail';
}

const WHOLE_SITE_WATER_LABELS: Readonly<Record<WholeSiteWaterLedgerKey, string>> = {
  siteWaterAllocationMgd: 'site water allocation',
  projectAverageWaterDemandMgd: 'average project water demand',
  projectPeakWaterDemandMgd: 'peak project water demand',
  projectWaterMarginMgd: 'project water allocation margin',
};

/** Ledger keys at a positive daily rate (a zero never identifies a source role). */
function wholeSiteRolesAt(ledger: readonly WholeSiteWaterLedgerEntry[], galPerDay: number | null | undefined): WholeSiteWaterLedgerKey[] {
  return galPerDay != null && Number.isFinite(galPerDay) && galPerDay > 0 ? matchWholeSiteWaterRoles(ledger, galPerDay) : [];
}

function wholeSiteLabel(keys: readonly WholeSiteWaterLedgerKey[]): string {
  return keys.map(key => WHOLE_SITE_WATER_LABELS[key]).join(' / ');
}

export const ZERO_MODELED_WATER_SUBTOTAL_NOTE = 'A zero modeled consumption subtotal does not establish zero site water use or that no water allocation is needed; '
  + 'whole-site demand is outside this equipment/process subtotal, so headroom and adequacy are withheld.';

/** W7 — document provenance exists for the declared input but its excerpt does not state that value as a consumptive-use limit. */
export const DOCUMENT_UNBOUND_WATER_INPUT_NOTE = 'The declared available-consumption input cites a document whose excerpt does not state that value as a consumptive-use limit; '
  + 'consumption headroom is withheld and adequacy is not established.';

/**
 * The whole-site ledger roles that make the declared available-consumption
 * input not a consumptive-use limit. Lane A's document role authority decides
 * first: a frozen document excerpt that binds the input as a consumptive-use
 * limit makes it a documented limit even at a ledger value. Ledger equality
 * decides only for an input with no document provenance; an unbound document
 * input is handled by the caller.
 */
function declaredInputWholeSiteRoles(
  provenance: DeclaredWaterConsumptionProvenance,
  ledger: readonly WholeSiteWaterLedgerEntry[],
  galPerDay: number | null | undefined,
): WholeSiteWaterLedgerKey[] {
  return provenance === 'documented-consumptive-limit' ? [] : wholeSiteRolesAt(ledger, galPerDay);
}

/**
 * W7 — role-safe consumption headroom and adequacy.
 *
 * The solver subtracts its equipment/process consumption subtotal from the
 * declared `availableWaterConsumptionGalPerDay` whatever role that input has.
 * When the input's role is not a consumptive-use limit, or a cooling/total row
 * equals a whole-site figure, or the subtotal is zero, the subtraction is not a
 * consumptive-use comparison: headroom is withheld and adequacy is
 * indeterminate. The input's role comes from its frozen document provenance
 * first (declaredWaterConsumptionProvenance): a bound consumptive-use limit is
 * a limit whatever the ledger says, and document provenance that does not bind
 * it withholds even without a ledger match. Without document provenance, the
 * current SED source ledger decides (an allocation, demand or margin is not a
 * limit). A real shortfall is kept: 'fail' survives when the input is the site
 * allocation, the subtotal is not itself a whole-site figure, and the subtotal
 * exceeds the allocation.
 * Rows are copied verbatim; the solver and its hashes are untouched. The
 * notes carry no numbers so the audit text never re-quotes a conflated rate.
 */
function roleSafeWaterVerdict(
  water: GenerationImpactScenarioSnapshot['result']['water'],
  available: number | undefined,
  ledger: readonly WholeSiteWaterLedgerEntry[],
  provenance: DeclaredWaterConsumptionProvenance,
): { headroom: number | null; adequacy: EngineeringVerdict; notes: string[] } {
  const total = water.totalConsumptionGalPerDay;
  const headroom = total == null || available == null ? null : available - total;
  const adequacy = waterAdequacy(total, available);
  const availableRoles = declaredInputWholeSiteRoles(provenance, ledger, available);
  const unboundDocumentInput = available != null && provenance === 'document-unbound' && !availableRoles.length;
  const rowRoles: Array<[string, WholeSiteWaterLedgerKey[]]> = ([
    ['data-center cooling withdrawal', water.dataCenterWithdrawalGalPerDay],
    ['data-center cooling consumption', water.dataCenterConsumptionGalPerDay],
    ['total withdrawal', water.totalWithdrawalGalPerDay],
    ['total consumption', water.totalConsumptionGalPerDay],
  ] as const).map(([label, value]): [string, WholeSiteWaterLedgerKey[]] => [label, wholeSiteRolesAt(ledger, value)])
    .filter(([, keys]) => keys.length > 0);
  const subtotalConflicted = rowRoles.length > 0;
  const zeroSubtotal = total === 0;
  if (!availableRoles.length && !unboundDocumentInput && !subtotalConflicted && !zeroSubtotal) return { headroom, adequacy, notes: [] };

  const realShortfall = adequacy === 'fail' && availableRoles.includes('siteWaterAllocationMgd') && !subtotalConflicted;
  const notes: string[] = [];
  if (availableRoles.length) {
    notes.push(`The declared available-consumption input equals the source-stated ${wholeSiteLabel(availableRoles)}, a whole-site quantity; `
      + 'a whole-site service allocation, demand or margin is not a consumptive-use limit, so consumption headroom is withheld'
      + (realShortfall
        ? ', and the modeled consumption subtotal alone already exceeds the whole-site allocation.'
        : ' and adequacy is not established.'));
  } else if (unboundDocumentInput) {
    notes.push(DOCUMENT_UNBOUND_WATER_INPUT_NOTE);
  }
  // Group rows that equal the same whole-site figure into one sentence.
  const byLabel = new Map<string, string[]>();
  for (const [row, keys] of rowRoles) byLabel.set(wholeSiteLabel(keys), [...(byLabel.get(wholeSiteLabel(keys)) ?? []), row]);
  for (const [label, rows] of byLabel) {
    const list = rows.length === 1 ? rows[0] : `${rows.slice(0, -1).join(', ')} and ${rows.at(-1)}`;
    notes.push(`Modeled ${list} ${rows.length === 1 ? 'equals' : 'equal'} the source-stated ${label}, a whole-site figure; `
      + 'cooling-specific consumption and makeup are not established by it.');
  }
  if (zeroSubtotal) notes.push(ZERO_MODELED_WATER_SUBTOTAL_NOTE);
  return { headroom: null, adequacy: realShortfall ? 'fail' : 'indeterminate', notes };
}

/**
 * Fuel-supply adequacy for a documented interconnect.
 *
 * Mirrors the `gas-interconnect-derate` solver branches so the report and the
 * risk register never disagree: an undocumented minimum delivery pressure is
 * indeterminate, peak above the derated capacity is a shortfall, and peak
 * inside the ideal-gas uncertainty band is indeterminate rather than a pass.
 */
function fuelSupplyProjection(
  snapshot: GenerationImpactScenarioSnapshot,
): GenerationImpactFuelSupplyProjection | null {
  const supply = snapshot.input.projectDutyBasis?.fuelGas?.supply;
  if (!supply) return null;
  const minimum = snapshot.input.projectDutyBasis?.fuelGas?.documentedSiteSupplyMinimum;
  const derate = deriveGasInterconnectDerate(supply, minimum);
  if (derate.status === 'not-computable') return null;
  const peak = snapshot.result.gas.peakDthPerDay;
  const derated = derate.deratedDeliverableDthPerDay;
  const adequacy: EngineeringVerdict = derated == null
    ? 'indeterminate'
    : peak > derated
      ? 'fail'
      : peak > derated - derate.uncertaintyDthPerDay
        ? 'indeterminate'
        : 'pass';
  return {
    quantityBasis: supply.quantityBasis,
    peakGasDthPerDay: peak,
    stationDthPerDay: derate.stationDthPerDay,
    contractDthPerDay: derate.contractDthPerDay,
    firmDeliverableDthPerDay: derate.firmDeliverableDthPerDay,
    firmBindingLimit: derate.firmBindingLimit,
    deratedDeliverableDthPerDay: derated,
    deratedBindingLimit: derate.deratedBindingLimit,
    headroomAtRatedPressureDthPerDay: derate.firmDeliverableDthPerDay == null
      ? null
      : derate.firmDeliverableDthPerDay - peak,
    headroomAtMinimumPressureDthPerDay: derated == null ? null : derated - peak,
    ratedAtPressureKPa: supply.ratedAtPressure.valueKPa,
    ratedAtPressureReference: supply.ratedAtPressure.reference,
    documentedMinimumDeliveryPressureKPa: minimum?.valueKPa ?? null,
    documentedMinimumDeliveryPressureReference: minimum?.reference ?? null,
    absolutePressureRatio: derate.absolutePressureRatio,
    compressibilityBasis: derate.compressibilityBasis,
    uncertaintyDthPerDay: derate.uncertaintyDthPerDay,
    adequacy,
    assumptions: [...derate.assumptions],
  };
}

/**
 * Read the user-selected contingency result while remaining compatible with
 * immutable snapshots written before the configurable N-k fields shipped.
 * Those snapshots have a complete N-1 result, so that is the only defensible
 * fallback; no capacity value is recomputed at report time.
 */
function selectedContingency(snapshot: GenerationImpactScenarioSnapshot): {
  criterion: string;
  outages: number;
  lostMW: number;
  netMW: number;
  loadMarginMW: number;
  reserveMarginMW: number;
  loadVerdict: EngineeringVerdict;
  reserveVerdict: EngineeringVerdict;
} {
  const capacity = snapshot.result.capacity;
  const outages = Number.isInteger(capacity.contingencyOutages) && capacity.contingencyOutages >= 0
    ? capacity.contingencyOutages
    : 1;
  return {
    criterion: outages === 0 ? 'N' : `N-${outages}`,
    outages,
    lostMW: Number.isFinite(capacity.contingencyLostMW)
      ? capacity.contingencyLostMW
      : capacity.largestContingencyMW,
    netMW: Number.isFinite(capacity.contingencyNetMW)
      ? capacity.contingencyNetMW
      : capacity.nMinusOneNetMW,
    loadMarginMW: Number.isFinite(capacity.contingencyMarginMW)
      ? capacity.contingencyMarginMW
      : capacity.nMinusOneMarginMW,
    reserveMarginMW: Number.isFinite(capacity.contingencyReserveMarginMW)
      ? capacity.contingencyReserveMarginMW
      : capacity.nMinusOneReserveMarginMW,
    loadVerdict: capacity.contingencyVerdict ?? capacity.nMinusOneVerdict,
    reserveVerdict: capacity.contingencyReserveVerdict ?? capacity.nMinusOneReserveVerdict,
  };
}

function goNoGoSummary(
  snapshot: GenerationImpactScenarioSnapshot,
  contingency: ReturnType<typeof selectedContingency>,
  screen: ReturnType<typeof resolveCapacityScreenVerdict>,
): string {
  const risks = snapshot.result.risks.length;
  // The lead sentence speaks the JOINT screen verdict: a load-pass with a
  // reserve shortfall must never summarize as a bare pass (Redhead case A).
  const capacityFinding = screen.screenVerdict === 'pass'
    ? `passes the ${contingency.criterion} screen on both the critical-load and reserve tests`
    : screen.screenVerdict === 'conditional'
      ? `passes the ${contingency.criterion} critical-load test but misses the reserve-padded requirement (conditional)`
      : screen.screenVerdict === 'fail'
        ? `falls short in the ${contingency.criterion} critical-load screen`
        : `is indeterminate for the ${contingency.criterion} screen`;
  return [
    `The promoted generation scenario “${snapshot.name}” ${capacityFinding}`,
    `with ${contingency.loadMarginMW.toFixed(1)} MW load margin and ${contingency.reserveMarginMW.toFixed(1)} MW reserve margin.`,
    `Planning footprint is ${snapshot.result.footprint.totalAcres.toFixed(2)} acres.`,
    modeledGenerationWaterConsumptionText(snapshot),
    `Modeled on-site generation residual CO2 is ${snapshot.result.emissions.residualCo2TonnesPerYear.toFixed(0)} tonnes/year (full-output capability basis). ${GENERATION_EMISSIONS_MODEL_SCOPE_NOTE}`,
    `${risks} deterministic risk${risks === 1 ? '' : 's'} remain. This capacity finding is an input to, not a replacement for, the overall site decision.`,
  ].join(' ');
}

export const GENERATION_WATER_MODEL_SCOPE_NOTE = 'This equipment/process subtotal does not quantify whole-site water demand. '
  + 'Makeup, initial fill, flushing, leakage, humidification and domestic uses outside the declared coefficients are not calculated by this subtotal.';

export const GENERATION_EMISSIONS_MODEL_SCOPE_NOTE = 'Modeled on-site generation emissions exclude purchased-grid electricity '
  + 'and any unmodeled standby operation; zero does not establish zero site emissions.';

/** Keep the frozen coefficient's actual provenance and declared scope in prose. */
function dataCenterCoolingCoefficientBasis(input: GenerationImpactScenarioInput): string {
  const value = input.water.dataCenterConsumptionGalPerMWhFacility;
  if (value == null) return 'Data-center cooling consumption intensity is not specified.';
  const origins = input.assumptions.filter(assumption =>
    assumption.field === 'water.dataCenterConsumptionGalPerMWhFacility' && assumption.value === value);
  const sourceLabels = {
    'planning-default': 'planning-default intensity',
    user: 'explicit owner assumption',
    document: 'document-stated coefficient',
    'site-evidence': 'site-evidence coefficient',
    dam: 'DAM-derived coefficient',
    'project-intent': 'Project Intent coefficient',
    vendor: 'vendor coefficient',
  } as const;
  const declaredOrigins = [...new Set(origins.map(assumption =>
    `${sourceLabels[assumption.source]}${assumption.confidence ? ` (${assumption.confidence} confidence)` : ''}`
    // Professional-source labels and citations can contain private intake or
    // file identities. Only the owner's declared operating assumption belongs
    // in this public reader-facing scope statement, not those source handles.
    + (assumption.source === 'user' && assumption.citation?.trim()
      ? `. Owner-declared scope: ${assumption.citation.trim()}` : '')))];
  return `Data-center cooling consumption coefficient: ${value.toLocaleString('en-US', { maximumFractionDigits: 10 })} gal/MWh of facility load. `
    + (declaredOrigins.length > 0
      ? declaredOrigins.join('; ')
      : 'No matching field-level source attribution is recorded in the frozen scenario.');
}

/** Read the frozen component sum without promoting it to whole-site demand. */
export function modeledGenerationWaterConsumptionText(snapshot: GenerationImpactScenarioSnapshot): string {
  const total = snapshot.result.water.totalConsumptionGalPerDay;
  return `Modeled generation, CCS and data-center cooling consumption is ${total == null ? 'indeterminate' : `${total.toFixed(0)} gal/day`} (equipment/process subtotal). `
    + `${GENERATION_WATER_MODEL_SCOPE_NOTE} ${dataCenterCoolingCoefficientBasis(snapshot.input)}`;
}

/** One comparison row, read straight from a frozen snapshot. Never recomputed. */
function comparisonRow(
  snapshot: GenerationImpactScenarioSnapshot,
  promoted: boolean,
): GenerationImpactComparisonRow {
  const { input, result } = snapshot;
  const capacity = result.capacity as typeof result.capacity & {
    contingencyOutages?: number;
    contingencyNetMW?: number;
    contingencyMarginMW?: number;
    contingencyReserveMarginMW?: number;
  };
  const outages = capacity.contingencyOutages ?? input.contingencyOutages ?? 1;
  const selection = activeGenerationEquipmentSelection(input);
  return {
    scenarioId: snapshot.id,
    scenarioName: snapshot.name,
    promoted,
    criterionLabel: outages === 0 ? 'N' : `N-${outages}`,
    criterionNetMW: capacity.contingencyNetMW ?? capacity.nMinusOneNetMW,
    loadMarginMW: capacity.contingencyMarginMW ?? capacity.nMinusOneMarginMW,
    reserveMarginMW: capacity.contingencyReserveMarginMW ?? capacity.nMinusOneReserveMarginMW,
    equipmentBasis: selection
      ? `${selection.manufacturer} ${selection.model}${selection.kind === 'user' ? ' (user-supplied, unverified)' : ''}${input.projectDutyBasis ? ' + project duty dossier' : ''}`
      : 'Planning technology (no vendor envelope applied)',
    peakGasMmbtuPerHour: result.gas.peakMmbtuPerHour,
    peakWaterConsumptionGalPerDay: result.water.totalConsumptionGalPerDay ?? null,
    footprintTotalAcres: result.footprint.totalAcres,
    inputHash: snapshot.inputHash,
    resultHash: snapshot.resultHash,
  };
}

/**
 * Freeze the promoted-vs-rejected screening comparison.
 *
 * Every number is READ from a frozen snapshot's own solver result — nothing is
 * recomputed here, so a row always says exactly what that scenario said when it
 * was saved. Returns null unless at least one rejected candidate resolved: a
 * "comparison" of one is not a comparison, and claiming otherwise would be the
 * kind of unearned assertion the report contract exists to prevent.
 */
export function buildGenerationImpactComparisonProjection(
  promoted: GenerationImpactScenarioSnapshot,
  compared: readonly GenerationImpactScenarioSnapshot[],
  unresolvedScenarioIds: readonly string[] = [],
): GenerationImpactComparisonProjection | null {
  const rejected = compared.filter(candidate => candidate.id !== promoted.id);
  if (rejected.length === 0) return null;
  const rows = [
    comparisonRow(promoted, true),
    ...rejected.map(candidate => comparisonRow(candidate, false)),
  ];
  const promotedRow = rows[0]!;
  const statement = `${promotedRow.scenarioName} was promoted from ${rows.length} screened configurations. `
    + `It carries ${promotedRow.equipmentBasis} and delivers ${promotedRow.criterionNetMW.toFixed(1)} MW under the `
    + `${promotedRow.criterionLabel} criterion, with a ${promotedRow.reserveMarginMW.toFixed(1)} MW reserve margin `
    + `on ${promotedRow.footprintTotalAcres.toFixed(1)} planning acres. `
    + `The remaining ${rejected.length} configuration${rejected.length === 1 ? ' is' : 's are'} recorded for comparison only and `
    + 'carr' + (rejected.length === 1 ? 'ies' : 'y') + ' no authority in this report.'
    + (unresolvedScenarioIds.length > 0
      ? ` ${unresolvedScenarioIds.length} compared scenario${unresolvedScenarioIds.length === 1 ? '' : 's'} could not be read and ${unresolvedScenarioIds.length === 1 ? 'is' : 'are'} omitted.`
      : '');
  return { rows, unresolvedScenarioIds: [...unresolvedScenarioIds], statement };
}

/**
 * P3.4 — state the relationship between the two water models, deterministically.
 * The screen catalog (0.15–0.36 L/kWh-IT, operator disclosures) models the SITE
 * boundary; the scenario duty models the EQUIPMENT / process boundary with
 * the frozen scenario's declared intensities and their source-specific scope. They
 * differ by construction, so the projection says so — the prose never has to,
 * and is forbidden to reconcile them (WATER-TRUTH-1 `water-basis-conflated`).
 */
export function waterDutyBasis(input: GenerationImpactScenarioInput): GenerationImpactWaterBasis {
  const dc = input.water.dataCenterConsumptionGalPerMWhFacility ?? null;
  const gen = input.water.generationConsumptionGalPerMWhGross;
  const genW = input.water.generationWithdrawalGalPerMWhGross;
  const ccs = input.ccs.enabled;
  const parts = [
    `generation-plant water at ${Math.round(genW).toLocaleString('en-US')} gal/MWh gross withdrawal and ${Math.round(gen).toLocaleString('en-US')} gal/MWh gross consumption`,
    ...(ccs ? ['CCS water per tonne captured'] : []),
    dataCenterCoolingCoefficientBasis(input),
  ];
  const dutyStatement = parts.join('; ');
  return {
    boundary: 'equipment-and-process',
    generationWithdrawalGalPerMWhGross: genW,
    generationConsumptionGalPerMWhGross: gen,
    dataCenterConsumptionGalPerMWhFacility: dc,
    ccsIncluded: ccs,
    statement:
      `Basis — modeled duty of the promoted scenario at the equipment and process boundary: ${dutyStatement}${/[.!?]$/.test(dutyStatement) ? ' ' : '. '}`
      + `${GENERATION_WATER_MODEL_SCOPE_NOTE} `
      + 'The Water Resource screening band is a different quantity — reported site-boundary water-use effectiveness per kWh of IT load, from operator disclosures — '
      + 'so the two are not comparable, are not summed, and are not reconciled by this report.',
  };
}

export function buildGenerationImpactReportProjection(
  reportType: ReportTypeId,
  snapshot: GenerationImpactScenarioSnapshot,
  context: GenerationImpactReportProjectionContext = {},
): GenerationImpactReportProjection | null {
  if (!isGenerationImpactReportType(reportType)) return null;
  const { input, result } = snapshot;
  const { capacity } = result;
  const contingency = selectedContingency(snapshot);
  const criterion = contingency.criterion;
  const reserveMarginPct = capacity.criticalLoadMW > 0
    ? Math.max(0, (capacity.reserveRequiredMW / capacity.criticalLoadMW - 1) * 100)
    : Math.max(0, input.reserveMarginPct ?? 15);
  const source = {
    scenarioId: snapshot.id,
    scenarioName: snapshot.name,
    ...(snapshot.targetPhaseId ? { targetPhaseId: snapshot.targetPhaseId } : {}),
    ...(snapshot.promotedAt ? { promotedAt: snapshot.promotedAt } : {}),
    inputHash: snapshot.inputHash,
    resultHash: snapshot.resultHash,
    schemaVersion: result.schemaVersion,
    modelVersion: result.modelVersion,
    ...(input.bomBasis ? {
      bomProvenance: {
        basisHash: input.bomBasis.basisHash,
        sourcePlanHash: input.bomBasis.sourcePlanHash,
        sourcePlanVersion: input.bomBasis.sourcePlanVersion,
        itemCount: input.bomBasis.items.length,
        totalNetNameplateMW: input.bomBasis.totalNetNameplateMW,
        capacityBasis: input.bomBasis.capacityBasis,
        solverBinding: input.bomBasis.solverBinding,
      },
    } : {}),
    ...(context.authority ? {
      layoutPublicationId: context.authority.provenance.layoutPublicationId,
      layoutVersion: context.authority.provenance.layoutVersion,
      layoutHash: context.authority.provenance.layoutHash,
      layoutPublishedAt: context.authority.provenance.layoutPublishedAt,
      sedAuthorityContractVersion: context.authority.contractVersion,
      parcelBoundarySchemaVersion: context.authority.provenance.parcelBoundarySchemaVersion,
      parcelBoundarySource: context.authority.provenance.parcelBoundarySource,
      parcelBoundaryHash: context.authority.provenance.parcelBoundaryHash,
      parcelBoundaryAreaAcres: context.authority.provenance.parcelBoundaryAreaAcres,
    } : {}),
  };

  // Screening-integrity annotations (Redhead review learnings): the joint
  // load+reserve verdict, the derate basis a verdict is only valid on, and
  // the capability-vs-served annualization label. Pure derivations from the
  // frozen snapshot; computed once and attached per report type below.
  const screen = resolveCapacityScreenVerdict({
    criterion,
    loadVerdict: contingency.loadVerdict,
    reserveVerdict: contingency.reserveVerdict,
    loadMarginMW: contingency.loadMarginMW,
    reserveMarginMW: contingency.reserveMarginMW,
  });
  const screeningBasis = deriveScreeningBasis(input.units);
  const gridImportOnly = isGridImportOnlyScenario(input);
  const annualization = deriveAnnualizationBasis({
    facilityLoadMW: input.facilityLoadMW,
    annualOperatingHours: input.annualOperatingHours,
    normalNetMW: capacity.normalNetMW,
    gridImportOnly,
  });

  const projection: GenerationImpactReportProjection = { reportType, source };
  // Method evidence, attached for every generation-bearing report type. The
  // authority (`source`) is untouched and still singular.
  const comparison = buildGenerationImpactComparisonProjection(
    snapshot,
    context.comparedScenarios ?? [],
    context.unresolvedComparedScenarioIds ?? [],
  );
  if (comparison) projection.comparison = comparison;
  // P3.2 (2026-09-05) — the water DUTY of the promoted scenario is an equipment
  // property, so it rides with Generation & Impact as well as with Water
  // Resource (which keeps the ENTITLEMENT half: rights, drought, permits,
  // conservation, cost). Same frozen figures, one builder, both report types.
  if (reportType === 'water-resource' || reportType === 'power-availability') {
    const available = input.availableWaterConsumptionGalPerDay;
    const basis = waterDutyBasis(input);
    const verdict = roleSafeWaterVerdict(result.water, available, wholeSiteWaterLedger(context.sed),
      declaredWaterConsumptionProvenance(input, available));
    projection.waterResource = {
      basis: verdict.notes.length ? { ...basis, statement: `${basis.statement} ${verdict.notes.join(' ')}` } : basis,
      availableConsumptionGalPerDay: available ?? null,
      ...result.water,
      consumptionHeadroomGalPerDay: verdict.headroom,
      adequacy: verdict.adequacy,
    };
  }
  if (reportType === 'feasibility' || reportType === 'power-availability') {
    const gridDependent = gridImportOnly || hasGridImportUnits(input);
    projection.capacityAndResilience = {
      ...(gridImportOnly ? { capacityRole: 'grid-supply' as const } : {}),
      ...(gridDependent ? { qualificationNote: GRID_SUPPLY_QUALIFICATION } : {}),
      criterion,
      contingencyOutages: contingency.outages,
      facilityLoadMW: input.facilityLoadMW,
      criticalLoadMW: capacity.criticalLoadMW,
      reserveMarginPct,
      reserveRequiredMW: capacity.reserveRequiredMW,
      normalNetMW: capacity.normalNetMW,
      contingencyLostMW: contingency.lostMW,
      contingencyNetMW: contingency.netMW,
      loadMarginMW: contingency.loadMarginMW,
      reserveMarginMW: contingency.reserveMarginMW,
      loadVerdict: contingency.loadVerdict,
      reserveVerdict: contingency.reserveVerdict,
      probabilityLoadServed: result.reliability.probabilityLoadServed,
      reliabilityCaveat: result.reliability.caveat,
      screenVerdict: screen.screenVerdict,
      screenStatement: screen.statement,
      screeningBasis,
      annualization,
      reliabilityPerspective: deriveReliabilityPerspective({
        probabilityLoadServed: result.reliability.probabilityLoadServed,
        tierTarget: context.sed?.fullProjectIntent?.datacenterTierTarget ?? null,
        gridImportOnly,
      }),
      ...(function attachGasDemand() {
        const gasDemand = deriveGasDemandPerspective({
          peakDthPerDay: result.gas.peakDthPerDay,
          annualMmbtu: result.gas.annualMmbtu,
          facilityLoadShare: annualization.facilityLoadShare,
        });
        return gasDemand ? { gasDemand } : {};
      })(),
    };
    // Gas deliverability is a constraint on the same power the capacity screen
    // asserts, so it rides with the capacity report types rather than getting
    // its own. Absent when the scenario documents no interconnect.
    const fuelSupply = fuelSupplyProjection(snapshot);
    if (fuelSupply) projection.fuelSupply = fuelSupply;
  } else if (reportType === 'test-fit') {
    const testFit = buildGenerationImpactTestFitProjection({
      scenario: snapshot,
      site: context.site,
      sed: context.sed,
      authority: context.authority,
    });
    const conventionNote = footprintConventionNote({
      plantAcres: result.footprint.plantAcres,
      unitResults: result.unitResults,
    });
    projection.testFitFootprint = conventionNote
      ? { ...testFit, footprintConventionNote: conventionNote }
      : testFit;
  } else if (reportType === 'environmental' || reportType === 'public-sentiment') {
    const receptorDistanceById = new Map(input.receptors.map((receptor) => [receptor.id, receptor.distanceM]));
    projection.environmentalAndCommunity = {
      annualization,
      ...result.emissions,
      noise: result.noise.map((row) => ({
        ...row,
        distanceM: receptorDistanceById.get(row.receptorId) ?? null,
      })),
      lighting: {
        ...result.lighting,
        planningFlags: [...result.lighting.planningFlags],
        fullyShielded: input.lighting.fullyShielded ?? null,
        bugRated: input.lighting.bugRated ?? null,
        motionOrCurfewControls: input.lighting.motionOrCurfewControls ?? null,
        iesPhotometricAvailable: input.lighting.iesPhotometricAvailable ?? null,
      },
    };
  } else if (reportType === 'executive-summary') {
    projection.goNoGoSummary = {
      capacityVerdict: contingency.loadVerdict,
      capacityScreenVerdict: screen.screenVerdict,
      criterion,
      loadMarginMW: contingency.loadMarginMW,
      reserveMarginMW: contingency.reserveMarginMW,
      totalWaterConsumptionGalPerDay: result.water.totalConsumptionGalPerDay,
      footprintAcres: result.footprint.totalAcres,
      residualCo2TonnesPerYear: result.emissions.residualCo2TonnesPerYear,
      risks: result.risks.map((risk) => ({ ...risk })),
      gaps: [...result.gaps],
      warnings: [...result.warnings],
      summary: goNoGoSummary(snapshot, contingency, screen),
    };
  }

  // SCREENING ECONOMICS — additive and optional (sponsor lesson: a
  // cost-silent report invites readers to invent their own numbers). Derives
  // from the frozen result + the SED fuel price; absent when either is
  // unusable, so legacy contexts project byte-identically.
  if (SCREENING_ECONOMICS_REPORT_TYPES.includes(reportType)) {
    const fuelPrice = context.sed?.fuelSupply?.data?.naturalGasPriceUsdPerMmbtu ?? null;
    const economics = deriveScreeningEconomics({
      fuelPriceUsdPerMmbtu: typeof fuelPrice === 'number' ? fuelPrice : null,
      fuelPriceSource: typeof fuelPrice === 'number' ? 'sed-fuel-supply' : 'unavailable',
      peakMmbtuPerHour: result.gas.peakMmbtuPerHour,
      normalNetMW: capacity.normalNetMW,
      annualOperatingHours: input.annualOperatingHours,
      facilityLoadShare: annualization.facilityLoadShare,
      capturedCo2TonnesPerYear: result.emissions.capturedCo2TonnesPerYear,
      technologies: [...new Set(input.units.map((unit) => unit.technology))],
    });
    if (economics) projection.screeningEconomics = economics;
  }

  // MAX-POWER ENVELOPE — additive, and DEGRADES rather than throws. The
  // authority check above throws on mismatch because a wrong scenario would
  // corrupt the whole projection; a stale max-power study is different — it is
  // one optional block, and failing an otherwise-good report over it would be
  // the wrong trade. So a mismatch OMITS the block.
  if (MAX_POWER_REPORT_TYPES.includes(reportType)) {
    const maxPower = projectMaxPowerEnvelope(snapshot, context);
    if (maxPower) projection.maxPowerEnvelope = maxPower;
    else {
      const reason = maxPowerWaterAuthorityReason(snapshot, context.maxPowerStudy, context.sed);
      if (reason) projection.maxPowerWithheldReason = reason;
    }
  }

  return projection;
}

/** Report types that carry the indicative screening-economics block. */
const SCREENING_ECONOMICS_REPORT_TYPES: readonly ReportTypeId[] = [
  'feasibility', 'power-availability', 'executive-summary',
];


/** Report types that carry the max-power block. */
const MAX_POWER_REPORT_TYPES: readonly ReportTypeId[] = [
  'feasibility', 'power-availability', 'test-fit', 'executive-summary',
];

/**
 * Flatten a stored study for rendering — or return null with the block simply
 * absent. Every rejection path here is a FRESHNESS check: the study must belong
 * to the scenario being reported, and must be the exact body the intent stamped.
 */
function projectMaxPowerEnvelope(
  snapshot: GenerationImpactScenarioSnapshot,
  context: GenerationImpactReportProjectionContext,
): GenerationImpactMaxPowerProjection | null {
  const study = context.maxPowerStudy;
  if (!study || study.status !== 'available' || !study.maximum || !study.boundQuality) return null;

  // The study must describe THIS scenario. A study computed against a different
  // revision would quote a capacity the rest of the report contradicts.
  if (study.basis.baselineInputHash !== snapshot.inputHash
    || study.basis.baselineResultHash !== snapshot.resultHash) {
    return null;
  }

  // When an intent stamp exists it must agree with the stored body, otherwise
  // the report would cite a figure Project Intent does not actually carry.
  const stamp = context.maxPowerStamp;
  if (stamp && (stamp.scenarioId !== snapshot.id
    || stamp.scenarioInputHash !== snapshot.inputHash
    || stamp.scenarioResultHash !== snapshot.resultHash)) {
    return null;
  }

  const m = study.maximum;
  if (maxPowerWaterAuthorityReason(snapshot, study, context.sed)) return null;

  return {
    modelVersion: study.modelVersion,
    boundQuality: study.boundQuality,
    qualifier: study.boundQuality === 'search-ceiling-reached' ? '≥' : '',
    firmNetMW: m.firmNetMW,
    isoGrossNameplateMW: m.isoGrossNameplateMW,
    siteGrossMW: m.siteGrossMW,
    blockCount: m.blockCount,
    isoGrossMWPerBlock: m.isoGrossMWPerBlock,
    facilityMW: m.facilityMW,
    facilityMWAtGenerationTerminals: m.facilityMWAtGenerationTerminals,
    itLoadMW: m.itLoadMW,
    apparentMVA: m.apparentMVA,
    pue: m.pue,
    pueSource: m.pueSource,
    tdLossPct: m.tdLossPct,
    bindingConstraintIds: [...study.bindingConstraintIds],
    bindingStatement: study.bindingStatement,
    landBasis: study.basis.landBasis,
    ceilings: study.ceilings.map((c): GenerationImpactMaxPowerProjection['ceilings'][number] => ({
      id: c.id, label: c.label, status: c.status,
      ceilingSiteGrossMW: c.ceilingSiteGrossMW,
      conservativeCeilingSiteGrossMW: c.conservativeCeilingSiteGrossMW,
      limitValue: c.limitValue, limitUnit: c.limitUnit, reason: c.reason,
    })),
    landFitNonMonotonic: study.landFitNonMonotonic,
    caveats: [...study.warnings],
  };
}

/** Mutates the in-flight report before its single persistence boundary. */
export function stampGenerationImpactOntoReport(
  report: SiteRagReport,
  reportType: ReportTypeId,
  snapshot: GenerationImpactScenarioSnapshot | null | undefined,
  context: GenerationImpactReportProjectionContext = {},
): SiteRagReport {
  // Normalize rather than merely append: a rerun must never inherit a stale
  // scenario from a parent report when no currently validated promotion exists.
  delete report.gridLedStandbyPlanningAtGen;
  delete report.inactiveGenerationImpactAuthorityAtGen;
  delete report.gridLedStandbyPlanningProjection;
  delete report.generationImpactScenarioAtGen;
  delete report.generationImpactProjection;
  if (reportType === 'test-fit' && context.testFitWithoutGenerationImpact) {
    return reconcileTestFitReportWithoutGenerationImpact(
      report,
      context.testFitWithoutGenerationImpact,
    );
  }
  if (!snapshot) return report;
  const scenarioForReport = context.authority?.scenarioSnapshot ?? snapshot;
  if (context.authority) {
    const authorityScenario = context.authority.scenarioSnapshot;
    if (authorityScenario.id !== snapshot.id
      || authorityScenario.siteId !== snapshot.siteId
      || authorityScenario.updatedAt !== snapshot.updatedAt
      || authorityScenario.inputHash !== snapshot.inputHash
      || authorityScenario.resultHash !== snapshot.resultHash
      || authorityScenario.result.modelVersion !== snapshot.result.modelVersion
      || authorityScenario.promotedAt !== snapshot.promotedAt) {
      throw new Error('Test Fit Generation & Impact authority does not match the promoted scenario');
    }
  }
  const projection = buildGenerationImpactReportProjection(reportType, scenarioForReport, context);
  if (!projection) return report;
  report.generationImpactScenarioAtGen = cloneSnapshot(scenarioForReport);
  report.generationImpactProjection = projection;
  if (reportType === 'test-fit' && projection.testFitFootprint) {
    reconcileTestFitReportWithGenerationImpact(
      report,
      scenarioForReport,
      projection.testFitFootprint,
      context.authority,
    );
  }
  return report;
}
